import React from 'react'
import { FoundationsConstants } from './FoundationsConstants'
declare let window: any
const UnicornApp = window.unicornInstance as any
import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios'

export function getBaseUrl() {
    return FoundationsConstants.BASE_URL
}

interface axiosProps {
    url: string
    data?: Object
    context?: Object
    success?: Function
    error?: Function
    beforeSend?: Function
    cache?: boolean
    triggerEvents?: {
        Started: boolean | String
        EndedSuccesfully: boolean | String
        EndedWithFailure: boolean | String
    }
    includeTokenHeader?: boolean
    Started?: Boolean
}

const axiosApi = axios.create({
    baseURL: getBaseUrl(),
})

const requestInterceptor = (config: AxiosRequestConfig) => {
    config.headers['Content-Type'] = 'application/json; charset=utf-8'
    return config
}
axiosApi.interceptors.request.use(requestInterceptor)

export async function FoundationsRequest(
    type: 'GET' | 'POST' | 'PUT' | 'DELTE',
    options: axiosProps
) {
    var originalSuccess = options.success
    var originalError = options.error
    var triggerEvents = options.triggerEvents

    if (UnicornApp.userFeedbackChannel) {
        UnicornApp.userFeedbackChannel.trigger(
            (triggerEvents && triggerEvents.Started) || 'HLModel:FetchStarted',
            {
                context: options.context,
            }
        )
    }

    const hl_user_token = options?.includeTokenHeader ? UnicornApp.getUserHLToken() : null

    let axiosHeaders: any = options.cache
        ? {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
              Expires: '0',
          }
        : {}

    if (hl_user_token !== null) axiosHeaders[FoundationsConstants.TOKEN_STR] = hl_user_token

    try {
        if (options.beforeSend !== undefined) options.beforeSend()
        const response: AxiosResponse =
            type === 'POST'
                ? await axiosApi.post(options.url, options.data, {
                      headers: axiosHeaders,
                  })
                : await axiosApi.get(options.url, {
                      headers: axiosHeaders,
                  })

        const data = await response.data
        if (UnicornApp.userFeedbackChannel) {
            UnicornApp.userFeedbackChannel.trigger(
                (triggerEvents && triggerEvents.EndedSuccesfully) ||
                    'UnicornRequest:EndedSuccesfully',
                {
                    data: data,
                    textstatus: response.statusText,
                    context: options.context,
                }
            )
        }
        if (originalSuccess !== undefined) {
            originalSuccess(data, response.statusText)
        }
    } catch (error) {
        const axiosError = error as AxiosError
        if (UnicornApp.userFeedbackChannel) {
            UnicornApp.userFeedbackChannel.trigger(
                (triggerEvents && triggerEvents.EndedWithFailure) ||
                    'UnicornRequest:EndedWithFailure',
                {
                    textStatus: axiosError.response?.statusText,
                    errorThrown: axiosError,
                    context: options.context,
                }
            )
        }

        if (originalError !== undefined) {
            originalError(axiosError.response?.statusText, axiosError)
        }
    }
}

export function formatPhone(phone: string): string {  // Formats a phone number to (XXX) XXX-XXXX and handles extensions
    if (!phone) return '';
    const extMatch = phone.match(/(?:x|ext)\s*(\d+)/i);
    const extension = extMatch ? extMatch[1] : '';
    const mainNumber = phone.replace(/(?:x|ext)\s*\d+/i, '').replace(/\D/g, '').slice(0, 10);

    if (mainNumber.length === 0) return '';
    const match = mainNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
    let formatted = '';
    if (match) {
        formatted = `(${match[1]}) ${match[2]}-${match[3]}`;
    } else if (mainNumber.length > 0) {
        formatted = `(${mainNumber.slice(0, 3)}`;
        if (mainNumber.length > 3) {
            formatted += `) ${mainNumber.slice(3, 6)}`;
        }
        if (mainNumber.length > 6) {
            formatted += `-${mainNumber.slice(6)}`;
        }
    }
    if (extension) {
        formatted += ` x${extension}`;
    }
    return formatted.trim();
}

export async function handleHardReload(url: string) {
    await fetch(url, {
        headers: {
            Pragma: 'no-cache',
            Expires: '-1',
            'Cache-Control': 'no-cache',
        },
    })
    window.location.href = url
    window.location.reload()
}
export function tzCodeToIANA(tzCode?: string): string  {
    if (!tzCode) return "";
    // Map tzCode to IANA time zone name
    const tzCodeToIANA: Record<string, string> = {
        'Eastern': 'America/New_York',
        'Central': 'America/Chicago',
        'Mountain': 'America/Denver',
        'Pacific': 'America/Los_Angeles',
        'Atlantic': 'America/Halifax',
        'Alaskan': 'America/Anchorage',
        'Hawaiian': 'Pacific/Honolulu',
        'US Mountain': 'America/Phoenix',
        'SA Western': 'America/Puerto_Rico',
    };
    return tzCodeToIANA[tzCode] ;
}

export function IANAToTzCode(iana?: string): string {
    if (!iana) return "";
    // Reverse map of tzCodeToIANA
    const ianaToTzCode: Record<string, string> = {
        'America/New_York': 'Eastern',
        'America/Chicago': 'Central',
        'America/Denver': 'Mountain',
        'America/Los_Angeles': 'Pacific',
        'America/Halifax': 'Atlantic',
        'America/Anchorage': 'Alaskan',
        'Pacific/Honolulu': 'Hawaiian',
        'America/Phoenix': 'US Mountain',
        'America/Puerto_Rico':'SA Western'
    };
    return ianaToTzCode[iana] || "";
}

export function getTimezoneOffset(tzCode?: string, dateInput: string | Date= new Date()): number {
    const date = new Date(dateInput);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone:tzCode,
      timeZoneName: 'shortOffset',
    });
  
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
  
    // Extract the offset like "GMT-4" or "GMT-5"
    const match = offsetPart?.value.match(/GMT([+-]\d+)/);
    if (!match){
         console.error('Could not determine timezone offset');
         return 0; // Default to 0 if we can't determine the offset
    }
  
    const offsetHours = parseInt(match[1], 10);
    return offsetHours * 60 * 60 * 1000; // Convert hours to milliseconds
  }
export function formatDateTimeLocal(date: Date | string | undefined): string {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Utility function to get the local timezone string
export function getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
export function formatDateInput(date: Date | string | undefined): string {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

export function formatTimeInput(date: Date | string | undefined): string {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toTimeString().slice(0, 5); // HH:mm
}

/**
 * Validates a date string based on the provided format
 * @param dateString - The date string to validate (e.g., "12/31/2023" or "12/31/2023 14:30")
 * @param dateFormat - The expected format (e.g., "MM/dd/yyyy" or "MM/dd/yyyy HH:mm")
 * @returns Error message if invalid, empty string if valid
 */
export function validateDateString(dateString: string, dateFormat: string): string {
    if (!dateString || dateString.replaceAll("/","").replaceAll("_","").trim() === '') {
        return ''; // Empty is valid (handled by required validation separately)
    }

    // Check if the string matches the expected format pattern
    const hasTime = dateFormat.includes('HH') || dateFormat.includes('mm');
    
    if (hasTime) {
        // Validate datetime format: MM/dd/yyyy HH:mm
        const datetimePattern = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/;
        const match = dateString.match(datetimePattern);
        
        if (!match) {
            return 'Invalid date and time format. Please use MM/DD/YYYY HH:mm';
        }
        
        const [, month, day, year, hour, minute] = match;
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);
        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);
        
        // Validate ranges
        if (monthNum < 1 || monthNum > 12) {
            return 'Invalid month. Month must be between 01 and 12';
        }
        if (dayNum < 1 || dayNum > 31) {
            return 'Invalid day. Day must be between 01 and 31';
        }
        if (yearNum < 1900 || yearNum > 2100) {
            return 'Invalid year. Year must be between 1900 and 2100';
        }
        if (hourNum < 0 || hourNum > 23) {
            return 'Invalid hour. Hour must be between 00 and 23';
        }
        if (minuteNum < 0 || minuteNum > 59) {
            return 'Invalid minute. Minute must be between 00 and 59';
        }
        
        // Validate actual date (e.g., Feb 30 is invalid)
        const date = new Date(yearNum, monthNum - 1, dayNum);
        if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
            return 'Invalid date. Please check the day and month';
        }
    } else {
        // Validate date format: MM/dd/yyyy
        const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const match = dateString.match(datePattern);
        
        if (!match) {
            return 'Invalid date format. Please use MM/DD/YYYY';
        }
        
        const [, month, day, year] = match;
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);
        
        // Validate ranges
        if (monthNum < 1 || monthNum > 12) {
            return 'Invalid month. Month must be between 01 and 12';
        }
        if (dayNum < 1 || dayNum > 31) {
            return 'Invalid day. Day must be between 01 and 31';
        }
        if (yearNum < 1900 || yearNum > 2100) {
            return 'Invalid year. Year must be between 1900 and 2100';
        }
        
        // Validate actual date (e.g., Feb 30 is invalid)
        const date = new Date(yearNum, monthNum - 1, dayNum);
        if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
            return 'Invalid date. Please check the day and month';
        }
    }
    
    return ''; // Valid
}

/**
 * Validates a time-only string (HH:mm format)
 * @param timeString - The time string to validate (e.g., "14:30")
 * @returns Error message if invalid, empty string if valid
 */
export function validateTimeString(timeString: string): string {
    if (!timeString || timeString.trim() === '') {
        return ''; // Empty is valid
    }
    
    const timePattern = /^(\d{2}):(\d{2})$/;
    const match = timeString.match(timePattern);
    
    if (!match) {
        return 'Invalid time format. Please use HH:mm';
    }
    
    const [, hour, minute] = match;
    const hourNum = parseInt(hour, 10);
    const minuteNum = parseInt(minute, 10);
    
    if (hourNum < 0 || hourNum > 23) {
        return 'Invalid hour. Hour must be between 00 and 23';
    }
    if (minuteNum < 0 || minuteNum > 59) {
        return 'Invalid minute. Minute must be between 00 and 59';
    }
    
    return ''; // Valid
}
//TODO1 : handle getting user object and saving in redux store globally
// public getCurrentUserFromAPI(forceFetch = false): JQuery.Deferred<any, any, any> {
//     var __this = this;
//     var defer = $.Deferred();
//     if (forceFetch) {
//       var me = new Me({ app: __this });

//       var myPromise = me.fetch({
//         success: function (response) {
//           if (response != null) {
//             var resultData = response.data;
//             if (resultData != null) {
//               if (resultData.MessageResults.length > 0) {
//                 for (var i = 0; i < resultData.MessageResults.length; i++) {
//                   if (resultData.MessageResults[i].HelpMessage == FoundationsEnums.Status.Success) {
//                     __this.toastr("success", resultData.MessageResults[i].Message);
//                   } else {
//                     __this.toastr("error", resultData.MessageResults[i].Message);
//                   }
//                 }
//               } else {
//                 __this.toastr("error", "Something went wrong");
//               }
//             }
//           }
//         },
//         error: function (err) {
//           console.log("error on fetching current user");
//           defer.reject(err);

//           //__this.redirectToSignIn();

//         },

//       });

//       myPromise.then(function (res) {
//         // __this.session.extendCookieTime("AuthToken"); // extend cookie session.

//         __this.globals.me = me;
//         defer.resolve(me);
//         __this.meState = "loaded";
//       });

//     } else {
//       if (this.meState != "loaded" && this.meState != "failed") {
//         if (this.meState != "loading") {
//           this.meState = "loading";
//           var me = new Me({ app: __this });

//           var myPromise = me.fetch({
//             success: function (response) {
//               if (response != null) {
//                 var resultData = response.attributes;
//                 if (resultData != null) {
//                   if (resultData.MessageResults.length > 0) {
//                     for (var i = 0; i < resultData.MessageResults.length; i++) {
//                       if (resultData.MessageResults[i].HelpMessage == FoundationsEnums.Status.Success) {

//                       } else {
//                         __this.toastr("error", resultData.MessageResults[i].Message)
//                       }
//                     }
//                   } else {
//                     this.toastr("error", "Something went wrong")
//                   }
//                 }
//               }
//             },
//             error: function (err) {
//               console.log("error on fetching current user");
//               defer.reject(err);

//               //__this.redirectToSignIn();

//             },

//           });

//           myPromise.then(function (res) {
//             // __this.session.extendCookieTime("AuthToken"); // extend cookie session.

//             __this.globals.me = me;
//             __this.meState = "loaded";
//             while (__this.mePromises.length) {
//               let d = __this.mePromises.pop();
//               d && d.resolve(me);
//             }
//           }, _ => {
//             __this.meState = "failed";
//             while (__this.mePromises.length) {
//               let d = __this.mePromises.pop();
//               d && d.reject();
//             }
//           });
//         }
//         this.mePromises.push(defer);
//         return defer;
//       } else if (this.meState == "loaded") {
//         setTimeout(function () {
//           //TODO! the issue is that this.globals.me should be cleared on sign out, or mestate should not be loaded
//           if (__this.globals.me)
//             defer.resolve(__this.globals.me);
//           else
//             defer.reject();
//         }, 0);
//       } else if (this.meState == "failed") {
//         setTimeout(function () {
//           defer.reject();
//         }, 0);
//       }
//     }

//     return defer;
//   }

// TODO 2 handle getting app settings nad storing globally in redux store

//   public getAppSettings(forceFetch = false): JQuery.Deferred<any, any, any> {
//     //Explanation to the code...
//     //We want to ensure only single call to Settings API, even though multiple handles may want to lay claim to the respurce.
//     //Of course, 'force' call is an exception.
//     //So, we maintain state of Settings API. If State is
//     // 1. 'just-start-of-page-load': make calls, but set the state to loading.
//     // 2. 'loading': then do nothing. By default, all promises are enqueued. Once finished fetching, reolve (or reject) everything in queue.
//     // 3. 'loaded': resolved with this.global.settings
//     // 4. 'failed': reject all promises
//     //I have unit tested this with a dozen simultaneous calls. For sure, only 1 request goes on wire.
//     //This is loosely based on logic for 'me' API, but this  is much cleaner. So, we should rewrite 'me' in this clean way.
//     //Also, if we are going to do this a lot, lets support it as a part of framework.
//     let defer = $.Deferred();
//     let __this = this;

//     if (forceFetch) {
//       let publicAppSettings = new PublicAppModel({ app: __this });

//       publicAppSettings.fetch().then(function (publicAppSettingsResponse) {
//         let privateAppSettings = new ProtectedAppModel({ app: __this });
//         if (__this.globals?.me || __this.getUserHLToken()) {
//           privateAppSettings.fetch().then(privateAppSettingsResponse => {
//             __this.globals.appSettings = new AppModel({ app: __this });
//             __this.globals.appSettings.set({ ...publicAppSettingsResponse.data, ...privateAppSettingsResponse.data });
//             if (window.healthLevelGlobal) {
//               window.healthLevelGlobal.appSettings = __this.globals.appSettings.toJSON();
//             }
//             __this.resolveAllPromises(__this.settingsPromises, __this.globals.appSettings);

//             this.settingsState = "loaded";
//           })
//         } else {
//           __this.globals.appSettings = new AppModel({ app: __this });
//           __this.globals.appSettings.set(publicAppSettingsResponse.data);

//           if (window.healthLevelGlobal) {
//             window.healthLevelGlobal.appSettings = __this.globals.appSettings.toJSON();
//           }

//           __this.resolveAllPromises(__this.settingsPromises, __this.globals.appSettings);
//           this.settingsState = "loaded";
//         }

//       }, (err) => {
//         __this.clearUserSessionAndRedirect()
//         console.log("error on fetching app settings");
//         __this.rejectAllPromises(__this.settingsPromises, err);
//       });

//     } else {
//       if (__this.settingsState == "loaded") {
//         setTimeout(function () {
//           __this.resolveAllPromises(__this.settingsPromises, __this.globals.appSettings);
//         }, 0);
//       } else if (__this.settingsState == "failed") {
//         __this.rejectAllPromises(__this.settingsPromises, undefined);
//       } else if (__this.settingsState == "loading") {
//       } else {
//         __this.settingsState = "loading";

//         let publicAppSettings = new PublicAppModel({ app: __this });

//         publicAppSettings.fetch().then(function (publicAppSettingsResponse) {
//           let privateAppSettings = new ProtectedAppModel({ app: __this });
//           if (__this.globals?.me || __this.getUserHLToken()) {
//             privateAppSettings.fetch().then(privateAppSettingsResponse => {
//               __this.globals.appSettings = new AppModel({ app: __this });
//               __this.globals.appSettings.set({ ...publicAppSettingsResponse.data, ...privateAppSettingsResponse.data });
//               if (window.healthLevelGlobal) {
//                 window.healthLevelGlobal.appSettings = __this.globals.appSettings.toJSON();
//               }
//               __this.resolveAllPromises(__this.settingsPromises, __this.globals.appSettings);

//               __this.settingsState = "loaded";
//             })
//           } else {
//             __this.globals.appSettings = new AppModel({ app: __this });
//             __this.globals.appSettings.set(publicAppSettingsResponse.data);

//             if (window.healthLevelGlobal) {
//               window.healthLevelGlobal.appSettings = __this.globals.appSettings.toJSON();
//             }

//             __this.resolveAllPromises(__this.settingsPromises, __this.globals.appSettings);
//             __this.settingsState = "loaded";
//           }

//         }, (err) => {
//           __this.clearUserSessionAndRedirect()
//           console.log("error on fetching app settings");
//           __this.rejectAllPromises(__this.settingsPromises, err);
//           __this.settingsState = "failed";
//         });
//       }

//     }
//     __this.settingsPromises.push(defer);
//     return defer;
//   }

// TODO3 handle toastr messages in react

// toastr(type: 'success' | 'error' | 'clear' | 'warning' = 'success', message = '', option: any = {}) {
//     (<any>window.toastr).options = {
//       preventDuplicates: option.preventDuplicates || true,
//       closeButton: option.closeButton || false,
//       newestOnTop: option.newestOnTop || false,
//       progressBar: option.progressBar || false,
//       positionClass: option.positionClass || "toast-bottom-right",
//       onclick: option.onclick || null,
//       showDuration: option.showDuration || 300,
//       hideDuration: option.hideDuration || 2000,
//       timeOut: option.timeOut != undefined ? option.timeOut : 2500,
//       extendedTimeOut: option.extendedTimeOut != undefined ? option.extendedTimeOut : 1000,
//       showEasing: option.showEasing || "swing",
//       hideEasing: option.hideEasing || "linear",
//       showMethod: option.showMethod || "fadeIn",
//       hideMethod: option.hideMethod || "fadeOut",
//     };
//     var currentToast;
//     if (type === 'clear') {
//       this.countdownToastrShown = false;
//       currentToast = (<any>window.toastr)[type]();
//     } else {
//       currentToast = (<any>window.toastr)[type](message);
//     }
//     return currentToast;
//   }

interface OpenIDParams {
    setPageError?: (msg: string) => void;
    setOpenIdLoading?: (loading: boolean) => void;
    setRequestSent?: (sent: boolean) => void;
}

export const handleSSOSignIn = ({
    setPageError,
    setOpenIdLoading,
    setRequestSent,
}: OpenIDParams = {}) => {
    setPageError?.('');
    setOpenIdLoading?.(true);
    setRequestSent?.(true);
    
    FoundationsRequest('GET', {
        url: 'api/v3/account/SSOLogin',
        cache: false,
        success: function (response: { data: any } | null) {
            if (response != null) {
                const authRequest = response.data;
                if (authRequest != null) {
                    const openIDURL =
                        authRequest.OpenIdLoginURL +
                        authRequest.OpenIdRedirectURL +
                        '&scope=' +
                        authRequest.scope +
                        '&client_id=' +
                        authRequest.client_id +
                        '&state=' +
                        authRequest.state;

                    UnicornApp.setCookie(
                        FoundationsConstants.OPENID_STATE,
                        authRequest.state,
                        10
                    );

                    window.location.href = openIDURL;
                }
            }
        },
        error: function (
            request: { responseJSON: { hints: any; statusMessage: any } },
            err: any
        ) {
            setPageError?.(
                request.responseJSON?.hints ??
                request.responseJSON?.statusMessage ??
                'An error occurred during SSO.'
            );
            setOpenIdLoading?.(false);
            setRequestSent?.(false);
        },
    });
};
